import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { Insole } from "~/api/insoles";
import { getInsoles } from "~/api/insoles";
import {
  AddInsoleModal,
  Header,
  PageWrapper,
  PaginatedTable,
} from "~/components";
import { DeleteInsoleConfirmationModal } from "~/components/Insoles/DeleteInsoleConfirmationModal";
import { InsoleStatusBadge } from "~/components/Insoles/InsoleStatusBadge";
import { usePaginatedQuery, useTableSearchParams } from "~/hooks";
import { ROUTES } from "~/router";
import { DropdownMenu } from "~/ui";
import { SearchInput } from "~/ui/SearchInput";

const PAGE_SIZE = 8;

export const MODAL_TYPE_OPTIONS = {
  ADD: "add",
  DELETE: "delete",
} as const;

export type ModalType =
  (typeof MODAL_TYPE_OPTIONS)[keyof typeof MODAL_TYPE_OPTIONS];

export const Insoles = () => {
  const { t } = useTranslation();

  const {
    currentPage,
    setCurrentPage,
    sorting,
    orderBy,
    sortDirection,
    setSorting,
    setSearchText,
    search,
  } = useTableSearchParams();
  const [selectedInsole, setSelectedInsole] = useState<Insole>();
  const [modalOpen, setModalOpen] = useState<ModalType>();

  const {
    query: { data, isLoading },
    totalItems,
  } = usePaginatedQuery({
    queryFn: () =>
      getInsoles({
        page: currentPage,
        per_page: PAGE_SIZE,
        search,
        order_by: orderBy,
        sort_direction: sortDirection,
      }),
    queryKey: [
      "getInsoles",
      {
        page: currentPage,
        pageSize: PAGE_SIZE,
        search,
        orderBy,
        sortDirection,
      },
    ],
  });

  const insoles = data?.data ?? [];

  const handleDeleteInsole = (insole: Insole) => {
    setSelectedInsole(insole);
    setModalOpen(MODAL_TYPE_OPTIONS.DELETE);
  };

  const columns: ColumnDef<Insole>[] = [
    {
      header: t("insoles.serial_number"),
      accessorKey: "serial_number",
    },
    {
      header: t("insoles.patient"),
      accessorKey: "paired_with",
      cell: ({ row }) => {
        const patient = row.original.patient;
        return patient ? (
          <Link
            to={ROUTES.patientDetail.replace(":id", patient.id.toString())}
            className="text-primary-40 underline dark:text-primary-80"
          >
            {patient.name} {patient.surname}
          </Link>
        ) : (
          <span>—</span>
        );
      },
    },
    {
      header: t("insoles.insole_gender"),
      accessorKey: "gender",
      accessorFn: (insole) => t(`insole_gender.${insole.gender}`),
    },
    {
      header: t("insoles.foot_size"),
      accessorKey: "size",
      accessorFn: (insole) => insole.shoe_size_us / 10,
    },
    {
      header: t("insoles.arch_type"),
      accessorKey: "arch_type",
      accessorFn: (insole) => t(`insole_arch_type.${insole.arch_type}`),
    },
    {
      header: t("insoles.status"),
      accessorKey: "status",
      cell: ({ row }) => {
        return <InsoleStatusBadge status={row.original.status} />;
      },
    },
    {
      id: "actions",
      cell: ({ row: { original } }) => (
        <div className="flex justify-end">
          <DropdownMenu
            buttonAriaLabel={t("insoles.view_options_for_insole", {
              serialNumber: original.serial_number,
            })}
            options={[
              {
                label: t("insoles.delete_insole"),
                onClick: () => handleDeleteInsole(original),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const total = data?.pagination?.total;

  return (
    <PageWrapper>
      <div className="mb-4 flex w-full flex-col gap-3">
        <Header
          title={t("insoles.insoles")}
          button={{
            text: t("insoles.add_new_insole"),
            onClick: () => {
              setModalOpen(MODAL_TYPE_OPTIONS.ADD);
            },
          }}
          badgeText={
            total != undefined
              ? t("insoles.insole_count", {
                  count: total,
                })
              : undefined
          }
        />
        <SearchInput
          placeholder={t("insoles.search_insole_or_patient_placeholder")}
          onValueChange={setSearchText}
        />
      </div>
      <PaginatedTable
        data={insoles}
        columns={columns}
        isLoadingData={isLoading}
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        sorting={sorting}
        setSorting={setSorting}
        totalRows={totalItems}
      />
      {selectedInsole && (
        <DeleteInsoleConfirmationModal
          isOpen={modalOpen === MODAL_TYPE_OPTIONS.DELETE}
          insole={selectedInsole}
          onClose={() => setModalOpen(undefined)}
        />
      )}
      <AddInsoleModal
        isOpen={modalOpen === MODAL_TYPE_OPTIONS.ADD}
        onClose={() => setModalOpen(undefined)}
      />
    </PageWrapper>
  );
};
